import React from "react";
import { AlertTriangleIcon } from "lucide-react";

import { Card, CardContent } from "@component/card";
import { useSubscription } from "@context/subscription_context";

/**
 * "Current Billing Period" card — the active billing cycle + pricing plan for the
 * current company's subscription. Shared by the Billing/Subscription "Workspace
 * Activity" surfaces (recruiter hiring activity + training activity) so the card
 * stays identical across platforms. Self-contained: reads the active subscription
 * from SubscriptionContext (populated for both HR and training-agent).
 */
export default function CurrentBillingPeriodCard() {
    const { subscriptionActive } = useSubscription();

    // Stripe subscription set to cancel at the end of the current billing period.
    const isCancelling = Boolean(
        subscriptionActive?.subscription_details?.stripe?.cancel_at_period_end ||
            subscriptionActive?.cancel_at_period_end ||
            subscriptionActive?.subscription_status === "cancelling",
    );

    const formatBillingDate = (dateStr: string | null): string => {
        if (!dateStr) return "N/A";
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    };

    const formatBillingPeriod = (): string => {
        const sub = subscriptionActive;
        if (sub?.is_trial) {
            if (!sub?.billing_period_start) return sub?.periode || "N/A";
            const start = new Date(sub.billing_period_start);
            if (sub?.billing_period_end) {
                const end = new Date(sub.billing_period_end);
                const startStr = start.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
                const endStr = end.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
                return `${startStr} - ${endStr}`;
            }
            const startStr = start.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
            return `From ${startStr}`;
        }
        if (!sub?.billing_period_start || !sub?.billing_period_end) return sub?.periode || "N/A";
        const start = new Date(sub.billing_period_start);
        const end = new Date(sub.billing_period_end);
        const startStr = start.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
        const endStr = end.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
        return `${startStr} - ${endStr}`;
    };

    const getPricingPlanLabel = (): React.ReactNode => {
        if (subscriptionActive?.is_trial) return "Trial Workspace";
        const cadence = subscriptionActive?.payment_cadence;
        if (!cadence || cadence === "Trial") return subscriptionActive?.subscription_package_name || "Trial Workspace";
        const labels: Record<string, [string, string]> = {
            "Quarterly": ["Quarterly", "Billed every 3 months"],
            "Semi-Annually": ["Semi-Annual", "Billed every 6 months"],
            "Annually": ["Annual", "Billed every 12 months"],
        };
        const entry = labels[cadence];
        if (entry) return <>{entry[0]} <span className="text-gray-600 font-normal">({entry[1]})</span></>;
        return cadence;
    };

    return (
        <Card>
            <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h3 className="text-base font-semibold">Current Billing Period</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Your activity resets at the end of each billing cycle.
                        </p>
                    </div>
                </div>

                {isCancelling && !subscriptionActive?.is_expired && (
                    <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                        <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                        <p className="text-sm text-amber-800">
                            Your subscription is set to cancel and will end on{" "}
                            <strong>
                                {formatBillingDate(
                                    subscriptionActive?.billing_period_end ||
                                        subscriptionActive?.ended_at ||
                                        null,
                                )}
                            </strong>
                            . You'll keep full access until then.
                        </p>
                    </div>
                )}

                <div className="mt-6 flex flex-wrap gap-x-36 gap-y-5">
                    {/* Billing Period */}
                    <div className="space-y-1">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Billing Period
                        </p>
                        <p className="text-sm font-bold">
                            {formatBillingPeriod()}
                        </p>
                    </div>

                    {/* Next Billing Date — non-trial only; hidden when the
                        subscription is cancelling (it won't renew). */}
                    {!subscriptionActive?.is_trial && !isCancelling && subscriptionActive?.next_billing_date && (
                        <div className="space-y-1">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                Next Billing Date
                            </p>
                            <p className="text-sm font-bold">
                                {formatBillingDate(subscriptionActive.next_billing_date)}
                                {subscriptionActive.days_remaining >= 0 && (
                                    <span className="text-muted-foreground font-normal ml-1">
                                        ({subscriptionActive.days_remaining} day{subscriptionActive.days_remaining !== 1 ? "s" : ""} remaining)
                                    </span>
                                )}
                            </p>
                        </div>
                    )}

                    {/* Pricing Plan */}
                    <div className="space-y-1">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Pricing Plan
                        </p>
                        <p className="text-sm font-bold">{getPricingPlanLabel()}</p>
                    </div>

                    {/* Next Payment Date — non-trial only; hidden when the
                        subscription is cancelling (it won't be charged again). */}
                    {!subscriptionActive?.is_trial && !isCancelling && subscriptionActive?.next_payment_date && (
                        <div className="space-y-1">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                Next Payment Date
                            </p>
                            <p className="text-sm font-bold">
                                {formatBillingDate(subscriptionActive.next_payment_date)}
                            </p>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
